import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dice5, Printer, ArrowLeft, Hash, UsersRound } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

const GROUP_TINTS = [
  { bg: "#EEF2FF", text: "#3730A3", border: "#C7D2FE" },
  { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0" },
  { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
  { bg: "#E0F2FE", text: "#075985", border: "#BAE6FD" },
  { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  { bg: "#CCFBF1", text: "#115E59", border: "#99F6E4" },
  { bg: "#FFEDD5", text: "#9A3412", border: "#FED7AA" },
];

export default function PrintView() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/session");
        setSession(data);
      } catch (err) {
        toast.error(formatErr(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!session) return null;

  const revealed = session.status === "revealed";
  const isGroups = revealed && session.draw_mode === "groups";
  const isNumbers = revealed && session.draw_mode === "numbers";

  const numbersSorted = [...session.students].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  const groupsMap = {};
  if (isGroups) {
    for (const s of session.students) {
      if (s.group == null) continue;
      groupsMap[s.group] = groupsMap[s.group] || [];
      groupsMap[s.group].push(s);
    }
  }

  const doPrint = () => window.print();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Print stylesheet + hide toolbar on print */}
      <style>{`
        @page { size: A4; margin: 14mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="print-back-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <button
            onClick={doPrint}
            data-testid="print-now-button"
            className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-slate-900/10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center rotate-3">
                <Dice5 className="w-4 h-4 text-white" />
              </div>
              <div className="font-display font-extrabold text-slate-900">Random Roster</div>
            </div>
            <h1 className="font-display text-4xl font-black tracking-tight text-slate-900">
              {session.teacher_name}
            </h1>
            <div className="text-sm text-slate-500 mt-1">
              Session <span className="font-mono-num font-bold">{session.code}</span> ·{" "}
              {session.students.length} student{session.students.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div className="font-bold uppercase tracking-widest text-indigo-600 mb-1">
              {isGroups ? "Groups Draw" : isNumbers ? "Numbers Draw" : "Roster"}
            </div>
            <div>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
          </div>
        </div>

        {!revealed && (
          <div className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">
            Reveal a draw first, then print this page.
          </div>
        )}

        {isNumbers && (
          <div data-testid="print-numbers-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {numbersSorted.map((s) => (
              <div
                key={s.id}
                className="border-2 border-slate-900/10 rounded-2xl p-5 flex items-center gap-4 bg-white"
              >
                <div className="font-mono-num text-5xl font-black text-indigo-600 leading-none">
                  {String(s.number).padStart(2, "0")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-2xl leading-none mb-1">{s.emoji || ""}</div>
                  <div className="font-display font-bold text-slate-900 truncate text-lg">
                    {s.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isGroups && (
          <div className="space-y-8" data-testid="print-groups-list">
            {(session.group_names || []).map((gname, idx) => {
              const g = idx + 1;
              const members = groupsMap[g] || [];
              const tint = GROUP_TINTS[idx % GROUP_TINTS.length];
              return (
                <div
                  key={g}
                  className="rounded-3xl p-6 border-2"
                  style={{ background: tint.bg, borderColor: tint.border }}
                >
                  <div className="flex items-baseline justify-between mb-4 pb-3 border-b" style={{ borderColor: tint.border }}>
                    <h2
                      className="font-display text-3xl font-black tracking-tight"
                      style={{ color: tint.text }}
                    >
                      #{g} · {gname}
                    </h2>
                    <div className="text-sm font-bold" style={{ color: tint.text }}>
                      {members.length} member{members.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {members.map((m) => (
                      <div
                        key={m.id}
                        className="bg-white/80 rounded-xl px-3 py-2 flex items-center gap-2 border"
                        style={{ borderColor: tint.border }}
                      >
                        <span className="text-xl">{m.emoji || ""}</span>
                        <span className="font-semibold text-slate-900 truncate">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center text-xs text-slate-400">
          {isGroups ? <UsersRound className="inline w-3 h-3 mr-1" /> : <Hash className="inline w-3 h-3 mr-1" />}
          Generated by Random Roster
        </div>
      </div>
    </div>
  );
}
